/**
 * TextSeriesDetail.jsx — Feb 9 2026.
 * Index page for a Text Series — shows the series cover, description,
 * and an ordered list of episodes. Tap an episode to open the reader.
 *
 * Feb 12 2026 user spec:
 *   - "edits to Book series has not been wired" → Edit chips on the
 *     series header + every episode row, gated to the owner / Kings /
 *     High King. Opens `BookSeriesEditModal`.
 *   - "add delete button for High King only" → delete chips on the
 *     header + every episode row, gated strictly to `role === 'admin'`.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, FileText, ChevronRight, Headphones, Sparkles, Coins,
  Pencil, Trash2, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, checkAuth } from '../utils/authUtils';
import BookSeriesEditModal from '../components/BookSeriesEditModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TextSeriesDetail = () => {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  // Edit / delete UI state.
  const [editMode, setEditMode] = useState(null); // null | 'series' | 'episode'
  const [editEpisode, setEditEpisode] = useState(null);
  const [deletingSeries, setDeletingSeries] = useState(false);
  const [deletingEpId, setDeletingEpId] = useState(null);

  const fetchAll = async () => {
    const r = await authFetch(`${BACKEND_URL}/api/text-series/${seriesId}`);
    if (r.ok) setData(await r.json());
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      try { setMe(await checkAuth()); } catch { /* anonymous OK */ }
      await fetchAll();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId]);

  if (loading) {
    return <main className="sharp-bg min-h-screen px-4 py-6 max-w-3xl mx-auto"><p className="text-white/40 text-sm">Loading…</p></main>;
  }
  if (!data) {
    return (
      <main className="sharp-bg min-h-screen px-4 py-6 max-w-3xl mx-auto">
        <p className="text-rose-300 text-sm">Series unavailable.</p>
        <button onClick={() => navigate('/library')} className="mt-3 text-amber-300 underline">Back to Library</button>
      </main>
    );
  }
  const { series, episodes } = data;
  const coverUrl = series.cover_image ? (series.cover_image.startsWith('http') ? series.cover_image : `${BACKEND_URL}${series.cover_image}`) : null;

  // Gate the edit/delete chips. High King + Kings see them on every
  // series; scribes see them only on their own series.
  const isRoyal = me?.role === 'admin' || me?.role === 'coadmin' || me?.role === 'king';
  const isHighKing = me?.role === 'admin'; // strict High King for delete
  const ownsSeries = me && (me.user_id === series.user_id || me.user_id === series.writer_id);
  const canEdit = isRoyal || ownsSeries;

  const deleteSeries = async () => {
    if (!confirm(`Delete the entire series "${series.title}" and all its episodes? This is permanent.`)) return;
    setDeletingSeries(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/text-series/${seriesId}`, { method: 'DELETE' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Delete failed');
        return;
      }
      toast.success('Series deleted');
      navigate('/book-series');
    } finally {
      setDeletingSeries(false);
    }
  };

  const deleteEpisode = async (ep) => {
    if (!confirm(`Delete "${ep.title}"? This is permanent.`)) return;
    setDeletingEpId(ep.episode_id);
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/text-series/${seriesId}/episodes/${ep.episode_id}`,
        { method: 'DELETE' },
      );
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Delete failed');
        return;
      }
      toast.success('Episode deleted');
      await fetchAll();
    } finally {
      setDeletingEpId(null);
    }
  };

  return (
    <main className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="text-series-detail-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/60 hover:text-white mb-5"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      <header className="mb-6 flex items-start gap-4">
        <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden bg-[#12131A] border border-white/10 flex-shrink-0 flex items-center justify-center">
          {coverUrl ? (
            <img src={coverUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <FileText className="w-9 h-9 text-indigo-300 opacity-60" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="eyebrow text-indigo-300">Text Series</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight mt-1">
            {series.title}<span className="text-indigo-300">.</span>
          </h1>
          <p className="text-[11px] text-white/45 mt-1 uppercase tracking-[0.15em] font-bold">
            by {series.author || 'Unknown'} · {episodes.length} {episodes.length === 1 ? 'episode' : 'episodes'}
            {series.genre && <> · {series.genre}</>}
          </p>

          {/* Scribe XP + coins (owner / King / High King only) */}
          {(typeof series.writer_xp_earned === 'number' && series.writer_xp_earned > 0) || (typeof series.writer_coins_earned === 'number' && series.writer_coins_earned > 0) ? (
            <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="text-series-writer-earnings">
              {series.writer_xp_earned > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-amber-200 bg-amber-400/10 border border-amber-400/30 rounded-full px-2 py-0.5 text-[11px] font-black uppercase tracking-widest tabular-nums"
                  data-testid="text-series-writer-xp-badge"
                  title="Scribe XP earned across this series"
                >
                  <Sparkles className="w-3 h-3" /> +{series.writer_xp_earned} XP
                </span>
              )}
              {series.writer_coins_earned > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-emerald-200 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2 py-0.5 text-[11px] font-black uppercase tracking-widest tabular-nums"
                  data-testid="text-series-writer-coins-badge"
                  title="Writer coins earned across this series"
                >
                  <Coins className="w-3 h-3" /> +{series.writer_coins_earned}
                </span>
              )}
            </div>
          ) : null}

          {/* Edit + Delete chips — Feb 12 2026. Edit = owner+royal; Delete = High King only. */}
          {(canEdit || isHighKing) && (
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="text-series-admin-actions">
              {canEdit && (
                <button
                  type="button"
                  onClick={() => { setEditMode('series'); setEditEpisode(null); }}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 hover:bg-amber-400/20 text-amber-200 px-3 py-1 text-[10px] font-black uppercase tracking-widest"
                  data-testid="text-series-edit-series-btn"
                >
                  <Pencil className="w-3 h-3" /> Edit series
                </button>
              )}
              {isHighKing && (
                <button
                  type="button"
                  onClick={deleteSeries}
                  disabled={deletingSeries}
                  className="inline-flex items-center gap-1 rounded-full border border-rose-400/40 bg-rose-400/10 hover:bg-rose-400/20 text-rose-200 disabled:opacity-60 px-3 py-1 text-[10px] font-black uppercase tracking-widest"
                  data-testid="text-series-delete-series-btn"
                  title="High King only — permanently delete the series"
                >
                  {deletingSeries ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {series.description && (
        <p className="text-sm text-white/75 mb-6" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          {series.description}
        </p>
      )}

      {episodes.length === 0 ? (
        <p className="text-center py-10 text-sm text-white/40" data-testid="text-series-no-episodes">
          No episodes yet.
        </p>
      ) : (
        <ol className="space-y-2.5" data-testid="text-series-episodes-list">
          {episodes.map((ep) => (
            <li
              key={ep.episode_id}
              className="rounded-2xl bg-[#12131A] border border-white/8 hover:border-indigo-400/40 transition-colors p-3 sm:p-4"
              data-testid={`text-series-episode-${ep.episode_id}`}
            >
              <div className="flex items-start gap-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1.5 tabular-nums w-8">
                  {String(ep.episode_number).padStart(2, '0')}
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/text-series/${seriesId}/episode/${ep.episode_id}`)}
                  className="flex-1 min-w-0 text-left"
                  data-testid={`text-series-episode-open-${ep.episode_id}`}
                >
                  <p className="font-display text-base sm:text-lg font-black text-white tracking-tight truncate">
                    {ep.title}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mt-1 tabular-nums inline-flex items-center gap-2">
                    <span>{ep.word_count} words</span>
                    <span className="text-white/30">·</span>
                    <span className="inline-flex items-center gap-1">
                      <Headphones className="w-3 h-3" /> TTS ready
                    </span>
                  </p>
                </button>

                {/* Per-episode Edit + Delete chips */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => { setEditMode('episode'); setEditEpisode(ep); }}
                      className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-amber-400/30 bg-amber-400/10 hover:bg-amber-400/20 text-amber-200"
                      data-testid={`text-series-edit-episode-${ep.episode_id}`}
                      title="Edit episode"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                  {isHighKing && (
                    <button
                      type="button"
                      onClick={() => deleteEpisode(ep)}
                      disabled={deletingEpId === ep.episode_id}
                      className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-rose-400/30 bg-rose-400/10 hover:bg-rose-400/20 text-rose-200 disabled:opacity-60"
                      data-testid={`text-series-delete-episode-${ep.episode_id}`}
                      title="High King only — delete episode"
                    >
                      {deletingEpId === ep.episode_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    </button>
                  )}
                  <ChevronRight className="w-4 h-4 text-indigo-300 ml-1" />
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {editMode && (
        <BookSeriesEditModal
          mode={editMode}
          series={series}
          episode={editEpisode}
          onClose={() => { setEditMode(null); setEditEpisode(null); }}
          onSaved={fetchAll}
        />
      )}
    </main>
  );
};

export default TextSeriesDetail;
